import { getApiClient } from '../axios-config';
import Logger from '../../../utils/logger';

/**
 * Projects API Service
 * Handles all API calls related to projects
 */
class ProjectsAPI {
  /**
   * Get all projects
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of all project objects
   */
  static async getAll(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/projects/');
    Logger.debug('ProjectsAPI.getAll - Response structure', {
      hasData: !!response.data.data,
      projectsCount: response.data.data?.projects?.length,
    });
    return response.data.data.projects || [];
  }

  /**
   * Get active projects
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of active project objects
   */
  static async getActive(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/projects/activeprojects');
    Logger.debug('ProjectsAPI.getActive - Response structure', {
      hasData: !!response.data.data,
      activeProjectsCount: response.data.data?.activeProjects?.length,
    });
    return response.data.data.activeProjects || [];
  }

  /**
   * Create a new project
   * @param {Object} project - Project object
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created project object
   */
  static async create(project, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/projects/addproject', {
      newProject: project,
    });
    return response.data.data.addedProject || project;
  }

  /**
   * Update an existing project
   * @param {Object} project - Project object with updates
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated project object
   */
  static async update(project, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put('/api/projects/modifyproject', {
      updatedProject: project,
    });
    return response.data.data.modifiedProject || project;
  }

  /**
   * Update multiple projects
   * @param {Array} projects - Array of project objects to update
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Update result
   */
  static async updateMultiple(projects, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put('/api/projects/updateprojects', {
      updatedProjects: projects,
    });
    return response.data.data;
  }

  /**
   * Get all workflows/step sets for a project
   * @param {string} projectId - ID of the project
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of workflows/step sets with nested statuses
   */
  static async getWorkflow(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(`/api/projects/${projectId}/workflow`);
    Logger.debug('ProjectsAPI.getWorkflow - Response structure', {
      hasData: !!response.data?.data,
      workflowsCount: response.data?.data?.workflows?.length,
    });
    return response.data.data.workflows || [];
  }

  /**
   * Update a native project workflow
   * @param {string} projectId - ID of the project
   * @param {string} stepSetId - ID of the status step set (workflow) to update
   * @param {Array} statuses - Array of updated status objects
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated workflow object
   */
  static async updateWorkflow(projectId, stepSetId, statuses, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put(
      `/api/projects/${projectId}/workflow/${stepSetId}`,
      { statuses }
    );
    Logger.debug('ProjectsAPI.updateWorkflow - Response structure', {
      hasData: !!response.data?.data,
    });
    return response.data.data.workflow;
  }

  /**
   * Manually initialize native workflow for a project
   * @param {string} projectId - ID of the project
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Initialization result
   */
  static async initializeWorkflow(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/projects/${projectId}/initialize-workflow`
    );
    Logger.debug('ProjectsAPI.initializeWorkflow - Response structure', {
      hasData: !!response.data?.data,
    });
    return response.data.data;
  }
}

export default ProjectsAPI;
